import dynamic from 'next/dynamic';
import { rem } from 'polished';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { BigPictureClient, Event as BPEvent, Fields } from '@vimeo/bigpicture-client';
import { useCapability } from '@vimeo/core/hooks/use-capability';
import { useGetUser } from '@vimeo/gctl-api/hooks/useUser';
import { translate } from '@vimeo/i18n-runtime';
import { PopOver } from '@vimeo/iris/components';
import { User } from '@vimeo/viewer';

import useOnClickOutside from '../../../hooks/use-outside-click/useOnClickOutside';
import { useQuotaWarningFreeUsers } from '../../../hooks/use-quota-warning-free-users/useQuotaWarningFreeUsers';
import { getPageName } from '../../utils/getEntryPage';
import { getQuotaMood, QuotaMood } from '../quota';
import { QuotaMenu } from './QuotaMenu';
import { StoragePill } from './StoragePill';

const UpsellModal = dynamic(() => import('@vimeo/upsell-modal'));

const UPLOAD_PAGE_PATH = `/upload/videos`;
const NINETY_PERCENT = 0.9;

const sendClick = (
  pageName: string,
  upsellName: Fields<'vimeo.upgrade_action', 19>['upsell_name'],
) => {
  BigPictureClient.sendEvent(
    new BPEvent('vimeo.upgrade_action', 19, {
      action_type: 'click',
      page: pageName,
      location: 'storage_widget',
      target: 'modal',
      upsell_name: upsellName,
      copy: 'video_usage',
      feature: 'quota',
      currency: null,
      device_type: null,
      discount_offer: null,
      duration: null,
      is_discount: null,
      is_new_pricing: null,
      loading_time: null,
      path: null,
      plan_selected: null,
      price: null,
      promo_code_id: null,
      purchase_type: null,
      target_path: null,
      upgrade_flags: null,
    }),
  );
};

interface Props {
  user?: User | null;
  jwt?: string;
  apiUrl?: string;
  teamOwnersId?: number | null;
  enableQuotaMenu?: boolean;
  onOpenQuotaMenu?: () => void;
  className?: string;
}

export const EssentialQuotaMenu = ({
  user,
  jwt,
  apiUrl,
  teamOwnersId,
  enableQuotaMenu = true,
  onOpenQuotaMenu,
  className,
}: Props) => {
  /**
   * TODO: ACT-299: Show quota warning widget to free users who've reached 50% of their quota
   * https://vimean.atlassian.net/browse/ACT-299
   */
  const getQuotaWarningFreeUsersBucket = useQuotaWarningFreeUsers();

  const quotaMenuRef = useRef(null);
  const [showQuotaMenu, setShowQuotaMenu] = useState<boolean | undefined>();
  const [isClickOutsideQuotaMenu, setIsClickOutsideQuotaMenu] = useState(false);
  const [isUpgradeUpsellOpen, setIsUpgradeUpsellOpen] = useState(false);

  const toggleQuotaMenu = useCallback(
    (open: boolean) => {
      setShowQuotaMenu(open);
      if (open) {
        onOpenQuotaMenu?.();
      }
    },
    [onOpenQuotaMenu],
  );

  const { capabilities, ready } = useCapability(
    ['hasLivePremium', 'hasEnterprise', 'hasTotalStorageCap', 'hasMonthlyBilling'],
    teamOwnersId,
  );

  const userId = teamOwnersId ?? user?.id;
  const { data: userResponse } = useGetUser(() => {
    if (!userId || !enableQuotaMenu) return null;

    return {
      where: { userId },
      select: ['membership.type', 'uploadQuota'],
      headers: { Accept: 'application/vnd.vimeo.*+json;version=3.4.14' },
    };
  });

  const uploadQuota = userResponse?.uploadQuota;
  const accountType = userResponse?.membership?.type;

  const useRestrictedQuota = uploadQuota?.restricted != null;
  const effectiveUsed = useRestrictedQuota ? uploadQuota.restricted.used : uploadQuota?.space?.used;
  const effectiveMax = useRestrictedQuota ? uploadQuota.restricted.max : uploadQuota?.space?.max;
  const effectiveUnit = useRestrictedQuota
    ? (uploadQuota.restricted.unit ?? 'video_size')
    : uploadQuota?.space?.unit;

  const pageName = getPageName(window.location.pathname);

  const quotaMood: QuotaMood = useMemo(() => {
    if (
      effectiveUnit &&
      effectiveMax !== null &&
      effectiveMax !== undefined &&
      effectiveMax >= 0 &&
      effectiveUsed !== undefined &&
      effectiveUsed >= 0
    ) {
      return getQuotaMood(effectiveUsed, effectiveMax, effectiveUnit);
    }

    return 'safe';
  }, [effectiveUsed, effectiveMax, effectiveUnit]);
  const usedStorageRatio =
    effectiveMax && effectiveMax > 0 && effectiveUsed !== undefined
      ? effectiveUsed / effectiveMax
      : 0;
  const isRedLevelStorageLimit = effectiveUnit === 'video_size' && usedStorageRatio >= 0.95;
  const resolvedPaywallFeature = isRedLevelStorageLimit ? 'storage_limit' : 'quota';

  const upsellModalConfig = useMemo(() => {
    const base = {
      mkcCode: '109339',
    };

    if (uploadQuota && (quotaMood === 'limit' || quotaMood === 'warn')) {
      if (
        effectiveMax !== null &&
        effectiveMax !== undefined &&
        effectiveUsed !== undefined &&
        effectiveUsed >= effectiveMax
      ) {
        return {
          headerText: translate('You’ve reached the video limit'),
          subHeaderText: translate('To get more videos, upgrade your account'),
          ...base,
        };
      } else {
        return {
          headerText: translate('You’ve almost reached the video limit'),
          subHeaderText: translate('To increase your video limit, upgrade your account'),
          ...base,
        };
      }
    }
    return base;
  }, [uploadQuota, quotaMood, effectiveMax, effectiveUsed]);

  useEffect(() => {
    // If menu has already been set to some deliberate state, do not change it.
    if (showQuotaMenu !== undefined) {
      return;
    }

    // checking if history is defined before checking the pathname allows this to work in storybook.
    if (typeof window !== 'undefined' && window.location.pathname !== UPLOAD_PAGE_PATH) {
      return;
    }

    // When quota mood is set to something other than safe, a modal will be used instead of the popover.
    if (quotaMood !== 'safe') {
      return;
    }

    if (!uploadQuota) {
      return;
    }

    const percentOfQuotaUsed =
      effectiveMax && effectiveUsed !== undefined ? effectiveUsed / effectiveMax : 0;
    const quotaWarningFreeUsersBucket =
      accountType && effectiveUsed !== undefined
        ? getQuotaWarningFreeUsersBucket(accountType, effectiveUsed)
        : null;

    const shouldShowQuotaMenu =
      quotaWarningFreeUsersBucket === 'variant' || percentOfQuotaUsed > NINETY_PERCENT;

    toggleQuotaMenu(shouldShowQuotaMenu);
  }, [
    accountType,
    effectiveMax,
    effectiveUsed,
    getQuotaWarningFreeUsersBucket,
    quotaMood,
    showQuotaMenu,
    toggleQuotaMenu,
    uploadQuota,
  ]);

  const teamOwnerId = useRef(teamOwnersId);
  useEffect(() => {
    if (teamOwnerId.current && teamOwnerId.current !== teamOwnersId) {
      toggleQuotaMenu(false); //Close quota menu everytime a new team is selected
      teamOwnerId.current = teamOwnersId;
    }
  }, [teamOwnersId, toggleQuotaMenu]);

  useOnClickOutside(
    [quotaMenuRef],
    () => {
      if (isClickOutsideQuotaMenu && showQuotaMenu) {
        toggleQuotaMenu(false);
      }
    },
    null,
    [showQuotaMenu, isClickOutsideQuotaMenu],
  );

  if (ready && uploadQuota) {
    return (
      <>
        <div ref={quotaMenuRef}>
          <StyledPopOver
            onMouseEnter={() => {
              setIsClickOutsideQuotaMenu(false);
            }}
            onMouseLeave={() => {
              setIsClickOutsideQuotaMenu(true);
            }}
            active={!!showQuotaMenu}
            attach={[
              [0, 0],
              [100, 0],
            ]}
            content={
              <QuotaMenu
                accountType={accountType}
                capabilities={capabilities}
                uploadQuota={uploadQuota}
                showing={!!showQuotaMenu}
                userInitiated={
                  !!showQuotaMenu &&
                  (!effectiveMax ||
                    (effectiveUsed !== undefined && effectiveUsed / effectiveMax <= NINETY_PERCENT))
                }
              />
            }
          >
            <div>
              <StoragePill
                used={effectiveUsed}
                max={effectiveMax ?? Infinity}
                period={
                  useRestrictedQuota
                    ? 'lifetime'
                    : uploadQuota.space.showing === 'periodic'
                      ? (uploadQuota.periodic.period ?? 'week')
                      : 'lifetime'
                }
                unit={effectiveUnit ?? 'video_size'}
                mood={quotaMood}
                textVariant={'ratio'}
                onClick={() => {
                  switch (quotaMood) {
                    case 'safe':
                      toggleQuotaMenu(!showQuotaMenu);
                      break;
                    case 'warn':
                      if (!isUpgradeUpsellOpen) {
                        setIsUpgradeUpsellOpen(true);
                        sendClick(pageName, 'approaching_quota');
                      }
                      break;
                    case 'limit':
                      if (!isUpgradeUpsellOpen) {
                        setIsUpgradeUpsellOpen(true);
                        sendClick(pageName, 'at_limit_quota');
                      }
                      break;
                  }
                }}
                className={className}
              />
            </div>
          </StyledPopOver>
        </div>
        {isUpgradeUpsellOpen && (
          <UpsellModal
            apiUrl={apiUrl}
            userConfig={{ jwt: jwt, userId: user?.id }}
            templateType="default"
            modalConfig={upsellModalConfig}
            onClose={() => setIsUpgradeUpsellOpen(false)}
            tracking={{
              params: {
                feature: 'quota',
                location: 'storage_widget',
                page: pageName,
                upsell_name:
                  quotaMood === 'warn'
                    ? 'approaching_quota'
                    : quotaMood === 'limit'
                      ? 'at_limit_quota'
                      : '',
              },
              paywallTracking: {
                paywallTrigger: 'footer_storage_widget_upgrade_button',
                paywallLocation: 'footer_storage_widget',
                paywallType: 'popup',
                paywallFeature: resolvedPaywallFeature,
              },
            }}
          />
        )}
      </>
    );
  }

  return <></>;
};

const StyledPopOver = styled(PopOver)`
  border-radius: ${rem(8)};
`;
